import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { 
  Bell,
  Send,
  Users,
  Shield,
  Crown,
  Headset,
  CreditCard,
  Clock,
  Loader2,
  RefreshCw,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Megaphone
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'all_users', label: 'All Users', icon: Users, color: 'bg-blue-500', description: 'Send to everyone' },
  { value: 'admins', label: 'Admins Only', icon: Shield, color: 'bg-purple-500', description: 'Admin team' },
  { value: 'support', label: 'Support Tickets', icon: Headset, color: 'bg-orange-500', description: 'Users with open tickets' },
  { value: 'vip', label: 'VIP Users', icon: Crown, color: 'bg-amber-500', description: 'VIP members only' },
  { value: 'trial', label: 'Trial Users', icon: Clock, color: 'bg-cyan-500', description: 'Users on free trial' },
  { value: 'paid', label: 'Paid Subscribers', icon: CreditCard, color: 'bg-green-500', description: 'Active subscribers' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-gray-500' },
  { value: 'normal', label: 'Normal', color: 'bg-blue-500' },
  { value: 'high', label: 'High', color: 'bg-orange-500' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-500' },
];

export default function BroadcastManager() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  
  // Form state
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('all_users');
  const [priority, setPriority] = useState('normal');

  const fetchBroadcasts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/broadcasts/`);
      setBroadcasts(response.data);
    } catch (err) {
      toast.error('Failed to load broadcasts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBroadcasts();
  }, []);

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/broadcasts/send`, {
        title,
        message,
        category,
        priority
      });
      
      toast.success(`Broadcast sent to ${response.data.recipient_count} users!`);
      
      // Reset form
      setTitle('');
      setMessage('');
      setCategory('all_users');
      setPriority('normal');
      setComposeOpen(false);
      
      // Refresh list
      fetchBroadcasts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedBroadcast) return;
    
    try {
      await axios.delete(`${API}/broadcasts/${selectedBroadcast.id}`);
      toast.success('Broadcast deleted');
      setDeleteConfirmOpen(false);
      setSelectedBroadcast(null);
      fetchBroadcasts();
    } catch (err) {
      toast.error('Failed to delete broadcast');
    }
  };

  const getCategoryInfo = (cat) => CATEGORIES.find(c => c.value === cat) || CATEGORIES[0];
  const getPriorityInfo = (pri) => PRIORITIES.find(p => p.value === pri) || PRIORITIES[1];

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="bg-gradient-to-r from-[#6D4AFF]/20 to-purple-500/10 border-[#6D4AFF]/30">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#6D4AFF]/20 rounded-xl">
                <Megaphone className="w-8 h-8 text-[#6D4AFF]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Broadcast Center</h2>
                <p className="text-gray-400">Send push notifications to users</p>
              </div>
            </div>
            <Button
              onClick={() => setComposeOpen(true)}
              className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
            >
              <Send className="w-4 h-4 mr-2" />
              New Broadcast
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Category Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          return (
            <Card key={cat.value} className="bg-[#1a1a2e] border-[#2d2d44]">
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <div className={`p-1.5 rounded-lg ${cat.color}/20`}>
                    <Icon className={`w-4 h-4 ${cat.color.replace('bg-', 'text-')}`} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">{cat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Broadcasts List */}
      <Card className="bg-[#1a1a2e] border-[#2d2d44]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white">Sent Broadcasts</CardTitle>
              <CardDescription className="text-gray-400">
                History of notifications sent to users
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={fetchBroadcasts}
              className="text-gray-400 hover:text-white"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#6D4AFF] animate-spin" />
            </div>
          ) : broadcasts.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No broadcasts sent yet</p>
              <p className="text-sm text-gray-500">Click &quot;New Broadcast&quot; to send your first notification</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#2d2d44]">
                    <TableHead className="text-gray-400">Title</TableHead>
                    <TableHead className="text-gray-400">Category</TableHead>
                    <TableHead className="text-gray-400">Priority</TableHead>
                    <TableHead className="text-gray-400">Recipients</TableHead>
                    <TableHead className="text-gray-400">Sent</TableHead>
                    <TableHead className="text-gray-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {broadcasts.map((broadcast) => {
                    const catInfo = getCategoryInfo(broadcast.category);
                    const priInfo = getPriorityInfo(broadcast.priority);
                    const CatIcon = catInfo.icon;
                    
                    return (
                      <TableRow key={broadcast.id} className="border-[#2d2d44] hover:bg-[#252540]">
                        <TableCell>
                          <div>
                            <p className="text-white font-medium">{broadcast.title}</p>
                            <p className="text-xs text-gray-500 truncate max-w-[200px]">
                              {broadcast.message}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CatIcon className={`w-4 h-4 ${catInfo.color.replace('bg-', 'text-')}`} />
                            <span className="text-gray-300 text-sm">{catInfo.label}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${priInfo.color} text-white text-xs`}>
                            {priInfo.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-gray-300">{broadcast.recipient_count}</span>
                        </TableCell>
                        <TableCell className="text-gray-400 text-sm">
                          {formatDate(broadcast.sent_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedBroadcast(broadcast);
                              setDeleteConfirmOpen(true);
                            }}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Compose Dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="bg-[#1a1a2e] border-[#2d2d44] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-[#6D4AFF]" />
              New Broadcast
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Send a push notification to selected users
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Category Selection */}
            <div className="space-y-2">
              <Label className="text-gray-300">Send To</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="bg-[#252540] border-[#2d2d44] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a2e] border-[#2d2d44]">
                  {CATEGORIES.map((cat) => {
                    const Icon = cat.icon;
                    return (
                      <SelectItem key={cat.value} value={cat.value} className="text-white">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${cat.color.replace('bg-', 'text-')}`} />
                          <span>{cat.label}</span>
                          <span className="text-gray-500 text-xs">- {cat.description}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Priority Selection */}
            <div className="space-y-2">
              <Label className="text-gray-300">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="bg-[#252540] border-[#2d2d44] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a2e] border-[#2d2d44]">
                  {PRIORITIES.map((pri) => (
                    <SelectItem key={pri.value} value={pri.value} className="text-white">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${pri.color}`} />
                        <span>{pri.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label className="text-gray-300">Title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Notification title..."
                className="bg-[#252540] border-[#2d2d44] text-white"
                maxLength={100}
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label className="text-gray-300">Message</Label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Your notification message..."
                className="bg-[#252540] border-[#2d2d44] text-white min-h-[100px]"
                maxLength={500}
              />
              <p className="text-xs text-gray-500 text-right">{message.length}/500</p>
            </div>

            {/* Preview */}
            {(title || message) && (
              <div className="p-4 bg-[#252540] rounded-lg border border-[#2d2d44]">
                <p className="text-xs text-gray-500 mb-2">Preview:</p>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#6D4AFF]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Bell className="w-5 h-5 text-[#6D4AFF]" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{title || 'Title'}</p>
                    <p className="text-sm text-gray-400">{message || 'Message...'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setComposeOpen(false)}
              className="text-gray-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending || !title.trim() || !message.trim()}
              className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Broadcast
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="bg-[#1a1a2e] border-[#2d2d44]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete Broadcast
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Are you sure you want to delete this broadcast? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {selectedBroadcast && (
            <div className="p-4 bg-[#252540] rounded-lg my-4">
              <p className="text-white font-medium">{selectedBroadcast.title}</p>
              <p className="text-sm text-gray-400">{selectedBroadcast.message}</p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDeleteConfirmOpen(false)}
              className="text-gray-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
